import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '@/auth'
import { profileService } from '@/services/profileService'
import type { UserHints, UserProfile } from '@/services/profileService'
import { imageUrlToSquarePngDataUrl } from '@/utils/image'

interface ProfileContextType {
  profile: UserProfile | null
  isLoading: boolean
  isUpdating: boolean
  error: Error | null
  needsCreate: boolean
  createDraft: UserHints & { displayName?: string; picture?: string }
  refetch: () => Promise<void>
  createProfile: (data: { displayName?: string; picture?: string }) => Promise<UserProfile>
  updateProfile: (data: Partial<Pick<UserProfile, 'displayName' | 'picture'>>) => Promise<UserProfile>
  displayName: string
  initials: string
}

const ProfileContext = createContext<ProfileContextType | null>(null)

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, refreshAccessToken } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [needsCreate, setNeedsCreate] = useState(false)
  const [createDraft, setCreateDraft] = useState<UserHints & { displayName?: string; picture?: string }>({})

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setProfile(null)
      setNeedsCreate(false)
      setCreateDraft({})
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await profileService.get()
      setProfile(data)
      setNeedsCreate(false)
      setCreateDraft({})
    } catch (err) {
      const apiErr = err as { status?: number; message?: string }
      if (apiErr?.status === 404) {
        // Profile does not exist yet: show "Create Profile" screen (no auto-create request).
        const email = user?.email
        const name = user?.name
        const tokenPictureUrl = user?.picture

        let picture: string | undefined
        if (tokenPictureUrl) {
          try {
            picture = await imageUrlToSquarePngDataUrl(tokenPictureUrl, 256)
          } catch {
            // If the picture URL isn't CORS-fetchable, we'll just omit it and let the user upload.
            picture = undefined
          }
        }

        setProfile(null)
        setNeedsCreate(true)
        setCreateDraft({
          email,
          name,
          displayName: name || (email ? email.split('@')[0] : undefined),
          picture,
        })
        setError(null)
      } else {
        console.error('Failed to fetch profile:', err)
        setError(err instanceof Error ? err : new Error(apiErr?.message || 'Failed to fetch profile'))
      }
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const createProfile = useCallback(async (data: { displayName?: string; picture?: string }) => {
    setIsLoading(true)
    setError(null)

    try {
      const created = await profileService.create(data)
      setProfile(created)
      setNeedsCreate(false)
      setCreateDraft({})

      // Refresh the app token now that we have a profile.
      // This swaps the Auth0 token for an app token with userId and permissions.
      await refreshAccessToken()

      return created
    } catch (err) {
      console.error('Failed to create profile:', err)
      setError(err instanceof Error ? err : new Error('Failed to create profile'))
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [refreshAccessToken])

  const updateProfile = useCallback(async (
    data: Partial<Pick<UserProfile, 'displayName' | 'picture'>>
  ) => {
    setIsUpdating(true)
    setError(null)

    try {
      const updated = await profileService.update(data)
      setProfile(updated)
      return updated
    } catch (err) {
      console.error('Failed to update profile:', err)
      setError(err instanceof Error ? err : new Error('Failed to update profile'))
      throw err
    } finally {
      setIsUpdating(false)
    }
  }, [])

  const displayName = profile?.displayName 
    || createDraft.displayName
    || createDraft.email?.split('@')[0]
    || 'User'

  const initials = (
    (profile?.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2)
      || createDraft.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2)
      || createDraft.email?.[0]
      || 'U')
  ).toUpperCase()

  return (
    <ProfileContext.Provider value={{
      profile,
      isLoading,
      isUpdating,
      error,
      needsCreate,
      createDraft,
      refetch: fetchProfile,
      createProfile,
      updateProfile,
      displayName,
      initials,
    }}>
      {children}
    </ProfileContext.Provider>
  )
}

export function useProfile() {
  const context = useContext(ProfileContext)
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}

